import { coerceMoneyString, coerceQuantity, normalizeDate } from "@/lib/processing/normalize";
import type { NormalizedRow } from "@/types";

/**
 * PSA adapter contract, mirroring the Python `AbstractPSAAdapter`. Adapters are
 * thin: they perform column mapping and status interpretation only. Value
 * normalization (currency, SKU cleaning, margins) lives in `processing/`.
 */

/** A raw CSV row: header name → cell value (missing cells may be `undefined`). */
export type RawRow = Record<string, string | undefined>;

/** Maps NormalizedRow field names to their source column headers. */
export type ColumnMapping = Record<string, string>;

/**
 * Build a {@link NormalizedRow} from `row` using `columnMapping`.
 *
 * Mirrors Python's `map_row_with_mapping`: string fields are copied verbatim
 * (so downstream quality rules can still observe issues like stray whitespace in
 * the vendor SKU); only price/cost/quantity are coerced and the date is parsed.
 * Missing columns become `null`/empty/zero, so mapping never throws on an
 * incomplete row. Computed margins are left `null` for the normalization step.
 */
export function mapRowWithMapping(row: RawRow, columnMapping: ColumnMapping): NormalizedRow {
  const source = (field: string): string | undefined => {
    const column = columnMapping[field];
    if (column === undefined) return undefined;
    return row[column];
  };
  return {
    product_id: source("product_id") ?? "",
    description: source("description") ?? "",
    price: coerceMoneyString(source("price")),
    cost: coerceMoneyString(source("cost")),
    manufacturer: source("manufacturer") ?? null,
    mfg_part: source("mfg_part") ?? null,
    vendor: source("vendor") ?? null,
    vendor_sku: source("vendor_sku") ?? null,
    on_hand: coerceQuantity(source("on_hand")),
    type: source("type") ?? null,
    category: source("category") ?? "",
    subcategory: source("subcategory") ?? null,
    status: source("status") ?? "",
    last_update: normalizeDate(source("last_update")),
    margin_dollar: null,
    margin_percent: null,
  };
}

/**
 * Contract implemented by every PSA adapter. `detect`, `mapRow`, and
 * `statusIsActive` are the core methods (mirroring Python's abstract methods);
 * `isActive` is derived — it operates on an already-mapped row and is used by
 * the filter pipeline.
 */
export interface PSAAdapter {
  readonly name: string;
  readonly version: string;
  readonly detectionSignatures: string[];
  /** Confidence in `[0, 1]` that this adapter handles `headers`. */
  detect(headers: string[]): number;
  /** Map a raw CSV row onto a {@link NormalizedRow}. */
  mapRow(row: RawRow): NormalizedRow;
  /** Whether a raw CSV row's status denotes an active product. */
  statusIsActive(row: RawRow): boolean;
  /** Whether an already-mapped row is active (used by filters). */
  isActive(row: NormalizedRow): boolean;
}

/** Shared base providing the derived {@link isActive} check over a mapped row. */
export abstract class BasePSAAdapter implements PSAAdapter {
  abstract readonly name: string;
  abstract readonly version: string;
  abstract detectionSignatures: string[];
  /** Casefolded status values considered active. */
  protected activeValues: Set<string> = new Set(["active"]);

  abstract detect(headers: string[]): number;
  abstract mapRow(row: RawRow): NormalizedRow;
  abstract statusIsActive(row: RawRow): boolean;

  isActive(row: NormalizedRow): boolean {
    const status = row.status.trim().toLowerCase();
    if (status === "") return true;
    return this.activeValues.has(status);
  }
}
